import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import gtm from 'general/js/gtm';
import ArrayHelper from 'foundation/array-helper';
import ScoringLoader, { endpointNames } from 'general/js/loaders/scoring-loader';
import cloneDeep from 'lodash/cloneDeep';
import {
    getPlayerSitecoreData, isRoundChoicesEqual,
    isRoundChoiceAvailable,
    setSelectedChoice
} from 'general/js/helpers';
import { BlockPlaceholder } from 'components/block-placeholder';
import { BlockSpinner } from 'components/block-spinner';
import { favoritePlayerService } from 'general/js/services';
import eventBus from 'general/js/event-bus';
import {
    EVENT_FAVOURITE_PLAYER_SERVICE_ADD,
    EVENT_FAVOURITE_PLAYER_SERVICE_REMOVE
} from 'general/js/services/favorite-player-services';
import { stateTypes } from './enums';
import LeaderboardWidgetFavorite from './leaderboard-widget-favorite';
import LeaderboardWidgetFavoriteAlphaList from './leaderboard-widget-favorite-alpha-list';

class LeaderboardWidgetFavoriteLayout extends React.Component {
    constructor(props) {
        super(props);

        this.players = [];
        this.topPlayersToShowState = true;
        this.numberOfCells = [
            { id: stateTypes.POSITION, textTitle: 'pos' },
            { id: stateTypes.NAME, textTitle: 'player' },
            { id: stateTypes.TEE },
            { id: stateTypes.TO_PAR, textTitle: 'par' },
            { id: stateTypes.HOLE, textTitle: 'hole' },
            { id: stateTypes.TODAY, textTitle: 'today' }
        ];
        this.state = {
            favoritePlayerList: favoritePlayerService.getAll(),
            applyFavourite: false
        };
    }

    componentDidMount() {
        ScoringLoader.subscribe(endpointNames.LIVE_SCANNER);
        ScoringLoader.subscribe(endpointNames.PLAYERS);
        ScoringLoader.subscribe(endpointNames.LEADERBOARD);
        eventBus.addListener(EVENT_FAVOURITE_PLAYER_SERVICE_ADD, this.renderFavoritePlayers);
        eventBus.addListener(EVENT_FAVOURITE_PLAYER_SERVICE_REMOVE, this.renderFavoritePlayers);
        this.updateChoices();
    }

    componentDidUpdate() {
        this.updateChoices();
    }

    componentWillUnmount() {
        ScoringLoader.unsubscribe(endpointNames.LIVE_SCANNER);
        ScoringLoader.unsubscribe(endpointNames.PLAYERS);
        ScoringLoader.unsubscribe(endpointNames.LEADERBOARD);
        eventBus.removeListener(EVENT_FAVOURITE_PLAYER_SERVICE_ADD, this.renderFavoritePlayers);
        eventBus.removeListener(EVENT_FAVOURITE_PLAYER_SERVICE_REMOVE, this.renderFavoritePlayers);
    }

    updateChoices() {
        const { liveScanner } = this.props;
        const { selectedRoundId } = this.state;

        if (!liveScanner) return;

        const prevChoices = this.state.roundChoices;
        const roundChoices = get(liveScanner, 'liveComponents.leaderboard.roundChoices');

        if (this.state.selectedRoundId &&
                isRoundChoiceAvailable(roundChoices, selectedRoundId)
        ) {
            setSelectedChoice(cloneDeep(roundChoices), selectedRoundId);

            if (!isRoundChoicesEqual(prevChoices, roundChoices)) {
                this.setState({ roundChoices });
            }
            return;
        }

        if (isRoundChoicesEqual(prevChoices, roundChoices)) return;

        this.setState({
            roundChoices,
            selectedRoundId: null,
        });
    }

    toggleFilterByFavourite = () => {
        if (!this.state.applyFavourite) {
            gtm.push('Leaderboard', 'Favourites', 'Select Favourites');
        }
        this.setState({ applyFavourite: !this.state.applyFavourite });
    }

    filterByFavorite = (playersData) => {
        return playersData.filter((player) =>
            favoritePlayerService.isFavourite(player.id.toString()));
    }

    renderFavoritePlayers = () => {
        this.setState({
            favoritePlayerList: favoritePlayerService.getAll()
        });
    }

    getPlayers = (players) => {
        const { options } = this.props;
        let playersList = [];
        const applyFavourite = this.state.applyFavourite;

        if (applyFavourite) {
            playersList = this.filterByFavorite(players);
            return playersList;
        }

        if (!ArrayHelper.isEmpty(options.playerIds)) {
            this.topPlayersToShowState = false;
            playersList = players.filter((player) => (options.playerIds.includes(player.id)));

            return playersList;
        }

        if (ArrayHelper.isEmpty(players)) { return playersList; }

        return players.slice(0, options.topPlayersToShow);
    }

    render() {
        const {
            options,
            liveScanner,
            scoringLeaderboard,
            sitecorePlayers
        } = this.props;

        if (liveScanner === undefined ||
            scoringLeaderboard === undefined ||
            sitecorePlayers === undefined
        ) {
            return (
                <BlockPlaceholder minHeight={300}>
                    <BlockSpinner />
                </BlockPlaceholder>);
        }

        const config = get(liveScanner, 'liveComponents.leaderboard');

        if (!config ||
            (options.type === 'contextual' && !config.ccpShow) ||
            !scoringLeaderboard ||
            !sitecorePlayers
        ) {
            return null;
        }

        if (liveScanner.showAlphaListOnLeaderboardWidget && ArrayHelper.isEmpty(scoringLeaderboard.players)) {
            return <LeaderboardWidgetFavoriteAlphaList
                options={options}
                players={sitecorePlayers.players}
            />;
        }


        this.players = this.getPlayers(scoringLeaderboard.players);

        return (

            <LeaderboardWidgetFavorite
                numberOfCells={this.numberOfCells}
                scoringLeaderboard={scoringLeaderboard}
                topPlayersToShowState={this.topPlayersToShowState}
                datePublished={''}
                titleText={options.textOverride}
                favoriteTitleText={options.textOverrideOnFavorite}
                players={this.players}
                fullLeaderboardLinkText={options.fullLeaderboardLinkText}
                fullLeaderboardLink={options.fullLeaderboardLink}
                targetsConfig={options.targetsConfig}
                patronLogo ={options.patronLogo}
                liveComponentLeaderboard = {config.show}
                liveComponentSubtitle = {config.subtitle}
                stateFavoriteButton={this.state.applyFavourite}
                onClick={this.toggleFilterByFavourite}
                liveScanner={liveScanner}
            />
        );
    }
}

LeaderboardWidgetFavoriteLayout.propTypes = {
    options: PropTypes.object
};

const mapStateToProps = (state) => {
    const sitecorePlayers = state.scoring[endpointNames.PLAYERS];
    const scoringLeaderboard = state.scoring[endpointNames.LEADERBOARD];

    if (sitecorePlayers === undefined) return {};

    // inject player sitecore data from players feed
    if (sitecorePlayers &&
        scoringLeaderboard &&
        !ArrayHelper.isEmpty(scoringLeaderboard.players)
    ) {
        scoringLeaderboard.players.forEach((player) => {
            player.sitecoreData = getPlayerSitecoreData(sitecorePlayers, player.id);
        });
    }

    return ({
        liveScanner: state.scoring[endpointNames.LIVE_SCANNER],
        scoringLeaderboard,
        sitecorePlayers,
    });
};

const LeaderboardWidgetFavoriteLayoutWrapper = connect(
    mapStateToProps,
    null
)(LeaderboardWidgetFavoriteLayout);

export default LeaderboardWidgetFavoriteLayoutWrapper;
